import { useEffect, useState } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import { TRIAL_BANNER_HEIGHT } from "app/components/React/TrialBanner/StyledTrialBanner";
import { AI_MEETING_AGENT_KB_ARTICLE_LINK } from "app/pages/sales-intelligence/constants/common";
import { AI_AGENT_MEETING } from "app/pages/sales-intelligence/constants/routes";
import { AIAgentCaption } from "app/pages/sales-intelligence/modules/ai-agent/components/AIAgentCaption";
import { AgentKnowledgeBase } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/AgentKnowledgeBase";
import { RedirectBriefModal } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/download-pdf/components/RedirectBriefModal/RedirectBriefModal";
import { AI_AGENT_PRICE_PER_RECORD } from "app/pages/sales-intelligence/modules/quota/constants";
import DataCreditsIndicatorContainer from "app/pages/sales-intelligence/modules/quota/containers/DataCreditsIndicatorContainer/DataCreditsIndicatorContainer";
import { LowQuotaBannerContainer } from "app/pages/sales-intelligence/modules/quota/containers/LowQuotaBannerContainer/LowQuotaBannerContainer";
import { useQuotaGuard } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaGuard";
import AIAgentPageLogo from "app/pages/sales-intelligence/pages/ai-agent/components/AIAgentPageLogo/AIAgentPageLogo";
import WelcomeTitle from "app/pages/sales-intelligence/pages/ai-agent/components/WelcomeTitle/WelcomeTitle";
import { AI_AGENT_PAGE_WIDTH } from "app/pages/sales-intelligence/pages/ai-agent/constants";
import { ActionsContainer } from "app/pages/sales-intelligence/pages/ai-agent/containers/ActionsContainer/ActionsContainer";
import MeetingCompanyAutocomplete from "app/pages/sales-intelligence/pages/ai-agent/containers/CompanyAutocomplete/MeetingCompanyAutocomplete";
import { isTrialOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const AIAgentMeetingGatePageContainer = () => {
    const isTrialBannerVisible = isTrialOrAfterTrial();
    const topOffset = isTrialBannerVisible ? TRIAL_BANNER_HEIGHT : 0;

    const [navigationData, setNavigationData] = useState<{
        domain: string;
        isCompanyLevel: boolean;
    } | null>(null);

    const { isBlocked } = useQuotaGuard(AI_AGENT_PRICE_PER_RECORD);

    const handleAnimationEnd = () => {
        void proNavigator.go(AI_AGENT_MEETING, {
            domain: navigationData?.domain,
            ...(navigationData?.isCompanyLevel ? { isCompanyLevel: navigationData?.isCompanyLevel } : {}),
        });
    };

    const handleNavigateWithFade = (nextDomain: string, isCompanyLevel: boolean) => {
        setNavigationData({ domain: nextDomain, isCompanyLevel });
    };

    useEffect(() => {
        if (navigationData?.domain) {
            const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

            if (prefersReducedMotion) {
                handleAnimationEnd();
            }
        }
    }, [navigationData?.domain]);

    return (
        <Flex
            flexDirection="column"
            alignItems="center"
            minHeight={`calc(100vh - ${topOffset}px)`}
        >
            <AIAgentPageLogo
                isLogoFading={!!navigationData?.domain}
                onAnimationEnd={handleAnimationEnd}
            />
            <ActionsContainer
                variant="gatePage"
                topOffset={topOffset}
            />
            <AgentKnowledgeBase
                top={topOffset + 26}
                helpLinkHref={AI_MEETING_AGENT_KB_ARTICLE_LINK}
            >
                <DataCreditsIndicatorContainer />
            </AgentKnowledgeBase>
            <Flex
                flex="1"
                padding="180px 0 0"
                flexDirection="column"
                alignItems="center"
                width={`${AI_AGENT_PAGE_WIDTH}px`}
                data-automation="ai-agent-gate-page-container"
            >
                <WelcomeTitle />
                <MeetingCompanyAutocomplete
                    onItemClick={handleNavigateWithFade}
                    isDisabled={isBlocked}
                />
                <LowQuotaBannerContainer />
            </Flex>
            <AIAgentCaption />
            <RedirectBriefModal />
        </Flex>
    );
};

export default AIAgentMeetingGatePageContainer;
